import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ProductCard } from "./PublicHome";
import { toast } from "sonner";
import { ShoppingBag, X, Plus, Minus, Search, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";

export default function PublicShop() {
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [cart, setCart] = useState([]);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const { customer } = useCustomerAuth();
  useEffect(() => {
    api.get("/public/products?tenant_slug=abrah").then(({ data }) => setProducts(data));
  }, []);
  const filtered = products.filter((p) =>
    !q || `${p.name} ${p.description || ""}`.toLowerCase().includes(q.toLowerCase()));
  const add = (p) => {
    setCart((c) => {
      const ex = c.find((x) => x.product_id === p.id);
      if (ex) return c.map((x) => x.product_id === p.id ? { ...x, quantity: x.quantity + 1 } : x);
      return [...c, { product_id: p.id, name: p.name, price: p.price, currency: p.currency || "UGX", quantity: 1 }];
    });
    toast.success(`Added ${p.name}`);
  };
  const setQty = (id, qty) => setCart((c) => c.map((x) =>
    x.product_id === id ? { ...x, quantity: Math.max(0, qty) } : x).filter((x) => x.quantity > 0));
  const subtotal = cart.reduce((s, x) => s + x.price * x.quantity, 0);
  const currency = cart[0]?.currency || "UGX";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-gray-900">Shop</h1>
          <p className="text-sm text-gray-600 mt-1">Order online — cash on delivery available. Create an account to track orders.</p>
        </div>
        {cart.length > 0 && (
          <Button onClick={() => setCheckoutOpen(true)}
            className="bg-emerald-900 hover:bg-emerald-950 text-white"
            data-testid="shop-checkout-button">
            <ShoppingBag size={14} className="mr-1.5" /> Cart ({cart.length}) · {currency} {subtotal.toLocaleString()}
          </Button>
        )}
      </div>

      {!customer && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-3 text-sm">
          <UserPlus size={16} className="text-amber-800 shrink-0" />
          <div className="flex-1 text-amber-900">
            <span className="font-semibold">Tip:</span>{" "}
            <Link to="/site/register" state={{ next: "/site/shop" }} className="underline font-semibold">Create a free account</Link>{" "}
            to track your orders and reorder faster next time.
          </div>
        </div>
      )}

      {checkoutOpen && (
        <CheckoutDialog
          cart={cart} setQty={setQty} subtotal={subtotal} currency={currency}
          customer={customer}
          onClose={() => setCheckoutOpen(false)}
          onPlaced={() => { setCart([]); setCheckoutOpen(false); }} />
      )}

      <div className="relative max-w-md mb-6">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input placeholder="Search products…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8" />
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-gray-300 rounded-md p-10 text-center">
          <ShoppingBag size={32} className="text-amber-700/40 mx-auto mb-2" />
          <div className="font-display text-lg text-gray-900">No products available yet</div>
          <div className="text-sm text-gray-600 mt-1">Check back soon — our admins will be uploading the catalogue.</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="shop-grid">
          {filtered.map((p) => <ProductCard key={p.id} p={p} onAdd={add} />)}
        </div>
      )}
    </div>
  );
}

function CheckoutDialog({ cart, setQty, subtotal, currency, customer, onClose, onPlaced }) {
  const [form, setForm] = useState({
    customer_name: customer?.full_name || "",
    customer_phone: customer?.phone || "",
    customer_email: customer?.email || "",
    delivery_address: customer?.delivery_address || "",
    branch: customer?.preferred_branch || "",
    payment_method: "cash_on_delivery",
    notes: "",
  });
  // Re-sync form when the customer prop hydrates late (auth /me still loading on mount)
  useEffect(() => {
    if (!customer) return;
    setForm((f) => ({
      ...f,
      customer_name: f.customer_name || customer.full_name || "",
      customer_phone: f.customer_phone || customer.phone || "",
      customer_email: f.customer_email || customer.email || "",
      delivery_address: f.delivery_address || customer.delivery_address || "",
      branch: f.branch || customer.preferred_branch || "",
    }));
  }, [customer]);
  const [branches, setBranches] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    // public site doesn't have an unauth branches endpoint; use tenant info or fall back
    setBranches(["Main Abrah", "New Abrah", "Abrah Jinja", "Abrah Arua Park", "Abrah Kikubo", "Old Abrah"]);
  }, []);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/public/orders", {
        tenant_slug: "abrah",
        ...form,
        items: cart.map((x) => ({ product_id: x.product_id, quantity: x.quantity })),
      });
      toast.success(`Order ${data.order_no} placed — we'll call you to confirm`);
      onPlaced();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Checkout</DialogTitle></DialogHeader>
        <div className="mb-4 border border-gray-200 rounded-md divide-y divide-gray-100">
          {cart.map((x) => (
            <div key={x.product_id} className="flex items-center gap-3 px-3 py-2 text-sm">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate">{x.name}</div>
                <div className="text-xs text-gray-500">{x.currency} {Number(x.price).toLocaleString()} each</div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setQty(x.product_id, x.quantity - 1)} className="h-6 w-6 rounded-md border border-gray-300"><Minus size={11} className="mx-auto" /></button>
                <span className="w-6 text-center">{x.quantity}</span>
                <button onClick={() => setQty(x.product_id, x.quantity + 1)} className="h-6 w-6 rounded-md border border-gray-300"><Plus size={11} className="mx-auto" /></button>
              </div>
              <div className="w-24 text-right font-semibold text-emerald-900">{x.currency} {(x.price * x.quantity).toLocaleString()}</div>
              <button onClick={() => setQty(x.product_id, 0)} className="text-red-700"><X size={13} /></button>
            </div>
          ))}
          <div className="flex justify-between font-display font-bold text-gray-900 px-3 py-3 bg-gray-50">
            <span>Total</span>
            <span>{currency} {subtotal.toLocaleString()}</span>
          </div>
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="checkout-form">
          <FieldLite label="Your name" required value={form.customer_name}
            onChange={(v) => setForm({ ...form, customer_name: v })} testid="checkout-name-input" />
          <FieldLite label="Phone" required value={form.customer_phone}
            onChange={(v) => setForm({ ...form, customer_phone: v })} testid="checkout-phone-input" />
          <FieldLite label="Email (optional)" value={form.customer_email}
            onChange={(v) => setForm({ ...form, customer_email: v })} />
          <div>
            <Label>Preferred branch</Label>
            <select className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })}>
              <option value="">Any branch</option>
              {branches.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <Label>Delivery address</Label>
            <Textarea rows={2} value={form.delivery_address}
              onChange={(e) => setForm({ ...form, delivery_address: e.target.value })} className="mt-1" />
          </div>
          <div className="md:col-span-2">
            <Label>Order notes (optional)</Label>
            <Textarea rows={2} value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" />
          </div>
          <DialogFooter className="md:col-span-2">
            <Button type="submit" disabled={busy}
              className="bg-emerald-900 hover:bg-emerald-950 text-white"
              data-testid="checkout-submit-button">
              {busy ? "Placing order…" : `Place order · ${currency} ${subtotal.toLocaleString()}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FieldLite({ label, value, onChange, required, testid }) {
  return (
    <div>
      <Label>{label}{required && <span className="text-red-600 ml-1">*</span>}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} required={required}
        className="mt-1" data-testid={testid} />
    </div>
  );
}
